/**
 * Council Store
 *
 * Centralized state management for the Council Debate feature.
 * Vanilla TypeScript with subscriber pattern (same pattern as simulationStore).
 * Strictly READ-ONLY — no trade execution, no portfolio impact.
 */

import type {
  CouncilState,
  CouncilStatus,
  NormalizedCouncilResult,
  CouncilErrorType,
  CouncilAnalysisRequest,
} from '../types/council';

import { runCouncilAnalysis as adapterRunAnalysis } from '../services/councilAdapter';

// ═══════════════════════════════════════════════════════════════════════
//  INITIAL STATE
// ═══════════════════════════════════════════════════════════════════════

const INITIAL_STATE: CouncilState = {
  status: 'idle',
  currentAnalysis: null,
  history: [],
  error: null,
  errorType: null,
  retryCount: 0,
};

// ═══════════════════════════════════════════════════════════════════════
//  STORE CLASS
// ═══════════════════════════════════════════════════════════════════════

type Listener = (state: CouncilState) => void;

class CouncilStoreClass {
  private state: CouncilState = { ...INITIAL_STATE };
  private listeners: Set<Listener> = new Set();

  // --- State access ---

  getState(): CouncilState {
    return { ...this.state };
  }

  // --- Subscriber pattern ---

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    const snapshot = this.getState();
    this.listeners.forEach((fn) => {
      try {
        fn(snapshot);
      } catch {
        // Swallow listener errors — never break store
      }
    });
  }

  // --- Mutations (private) ---

  private setState(partial: Partial<CouncilState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  // --- Actions ---

  async runAnalysis(request: CouncilAnalysisRequest): Promise<void> {
    // Guard: don't start if already loading
    if (this.state.status === 'loading' || this.state.status === 'retrying') {
      return;
    }

    this.setState({
      status: 'loading',
      error: null,
      errorType: null,
      retryCount: 0,
    });

    const result = await adapterRunAnalysis(request, (attempt) => {
      this.setState({
        status: 'retrying',
        retryCount: attempt,
      });
    });

    if (result.success && result.result) {
      this.setState({
        status: 'success',
        currentAnalysis: result.result,
        history: [result.result, ...this.state.history].slice(0, 50),
        error: null,
        errorType: null,
        retryCount: result.retriesUsed,
      });
    } else {
      this.setState({
        status: 'error',
        error: result.error ?? 'Analysis failed. Please retry.',
        errorType: result.errorType ?? 'unknown',
        retryCount: result.retriesUsed,
      });
    }
  }

  resetAnalysis(): void {
    this.setState({
      status: 'idle',
      currentAnalysis: null,
      error: null,
      errorType: null,
      retryCount: 0,
    });
  }
}

// ═══════════════════════════════════════════════════════════════════════
//  SINGLETON
// ═══════════════════════════════════════════════════════════════════════

export const councilStore = new CouncilStoreClass();
